import React from 'react';

/**
 * Componente para capturar a Inspeção do Meato Acústico Externo (MAE)
 * OBRIGATÓRIO segundo GUIA_LAUDO_AUDIOMETRICO_CFFA.md (CFFA 2020)
 * 
 * A inspeção detecta:
 * - Cerúmen (cera): pode causar perda de até 40dB (falso positivo)
 * - Corpos estranhos: bloqueiam canal auditivo
 * - Inflamação: otite externa, eczema, furúnculo
 * 
 * @param {Object} props
 * @param {string} props.odValue - Valor atual OD (orelha direita)
 * @param {string} props.oeValue - Valor atual OE (orelha esquerda)
 * @param {function} props.onChange - Callback (od, oe) => {}
 * @param {boolean} props.required - Se campo é obrigatório
 */
const MeatusInspectionField = ({ odValue, oeValue, onChange, required = true }) => {
  const options = [
    { value: '', label: '-- Selecione --' },
    { value: 'livre_pervio', label: 'Livre e pérvio' },
    { value: 'cerumen', label: 'Presença de cerúmen' },
    { value: 'corpo_estranho', label: 'Corpo estranho' },
    { value: 'inflamacao', label: 'Inflamação/otite externa' },
  ];

  const handleOdChange = (e) => {
    onChange(e.target.value, oeValue);
  };

  const handleOeChange = (e) => {
    onChange(odValue, e.target.value);
  };

  return (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-blue-800">
            Inspeção do Meato Acústico Externo (MAE) {required && <span className="text-red-500">*</span>}
          </h3>
          <div className="mt-2 text-sm text-blue-700">
            <p className="mb-3">
              <strong>OBRIGATÓRIO CFFA 2020:</strong> A inspeção visual do canal auditivo deve ser realizada 
              antes da audiometria para detectar cerúmen ou obstruções que possam causar perdas falsas (até 40dB).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Orelha Direita (OD) */}
              <div>
                <label htmlFor="meatus-od" className="block text-sm font-medium text-gray-700 mb-1">
                  Orelha Direita (OD)
                </label>
                <select
                  id="meatus-od"
                  value={odValue}
                  onChange={handleOdChange}
                  required={required}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Orelha Esquerda (OE) */}
              <div>
                <label htmlFor="meatus-oe" className="block text-sm font-medium text-gray-700 mb-1">
                  Orelha Esquerda (OE)
                </label>
                <select
                  id="meatus-oe"
                  value={oeValue}
                  onChange={handleOeChange}
                  required={required}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 text-xs text-blue-600">
              <strong>💡 Importância clínica:</strong> Se detectar cerúmen, remova antes da audiometria. 
              Cerúmen pode causar perda condutiva de até 40dB, gerando diagnóstico incorreto.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeatusInspectionField;
